import { useState, useEffect, useRef } from "react";
import { Bell, Check, ClipboardList, Clock, MessageCircle } from "lucide-react";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { urlForNotification } from "../../utils/notificationRouting";
import { titleCaseLabel, stripCountry } from "../../utils/helper";

export default function NotificationBell() {
  const [{ user }, dispatch] = useStateValue();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef();
  console.log(user);

  const userId = user?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getNotifications(user.id);
      
        setNotifications(data);
        console.log("noti", data);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchData();
  }, []);

  // const filteredNotifications = notifications.filter(
  //   (n) => n.taskid && n.assigneded === userId,
  // );

  const unreadNotifications = notifications.filter(
    (n) => n.read !== true,
  );

  const navigate = useNavigate();

  // Mark All Read, right in the dropdown — a pile of 18 shouldn't need 18
  // clicks (or a trip to the See All page) to clear the badge.
  const [markingAll, setMarkingAll] = useState(false);
  const handleMarkAllRead = async () => {
    if (markingAll || unreadNotifications.length === 0) return;
    setMarkingAll(true);
    try {
      const res = await markAllNotificationsAsRead(
        unreadNotifications.map((n) => n.id),
      );
      if (res.success) {
        setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
      }
    } catch (error) {
      console.error("Mark all read failed:", error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = async (n) => {
    try {
      if (!n.read) {
        await markNotificationAsRead(n.id);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === n.id ? { ...item, read: true } : item,
          ),
        );
      }
      setOpen(false);
      const url = urlForNotification(n);
      if (url) navigate(url);
    } catch (error) {
      console.error("Notification click error:", error);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(Number(timestamp));

    return date.toLocaleString(); // default readable format
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setOpen(!open)}
        className="border p-2 rounded-full cursor-pointer relative hover:bg-gray-100 transition"
      >
        <Bell className="text-[#1E293B] size-6 lg:size-5" />

        {unreadNotifications.length > 0 && (
          <div className="bg-red-500 text-white rounded-full size-4 flex items-center justify-center text-xs absolute -top-1 -right-1">
            {unreadNotifications.length}
          </div>
        )}
      </div>

      {open && (
        <div className="absolute right-0 mt-1 w-80 lg:w-96 bg-white rounded-2xl shadow-xl border z-50">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 text-lg">
              Notifications
            </h3>
            <div className="flex items-center gap-3">
              {unreadNotifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="text-xs font-semibold text-linkColor hover:text-themeColor disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Check size={13} />
                  {markingAll ? "Marking…" : "Mark All Read"}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/dashboard/notifications");
                }}
                className="text-xs font-semibold text-linkColor hover:text-themeColor"
              >
                See All
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className="px-4 py-2 hover:bg-gray-50 border-b cursor-pointer flex items-center justify-between gap-x-2"
                >
                  <div className="flex items-center gap-x-2">
                    {n.type === "deadline" ? (
                      <div className="bg-[#EEF2FF] text-red-500 size-8 flex items-center justify-center rounded-full shrink-0 group-hover:scale-105 transition">
                        <Clock size={18} />
                      </div>
                    ) : n.type === "mention" ? (
                      <div className="bg-[#EEF2FF] text-[#1E293B] size-8 flex items-center justify-center rounded-full shrink-0 group-hover:scale-105 transition">
                        <MessageCircle size={18} />
                      </div>
                    ) : (
                      <div className="bg-[#EEF2FF] text-[#1E293B] size-8 flex items-center justify-center rounded-full shrink-0 group-hover:scale-105 transition">
                        <ClipboardList size={18} />
                      </div>
                    )}
                    <div className="text-left">
                      <h4 className="text-xs lg:text-sm font-medium text-gray-800">
                        {titleCaseLabel(stripCountry(n.notificationtitle))}
                      </h4>
                      <p className="text-black/50 text-xs">
                        {stripCountry(n.notificationmessage)}
                      </p>
                      <p className="text-xs text-gray-400">
                         {moment(n.notificationid).fromNow()}
                       
                      </p>
                    </div>
                  </div>
                  {n.read !== true && (
                    <div className="bg-[#EEF2FF] text-green-500 size-3 flex items-center justify-center rounded-full shrink-0 group-hover:scale-105 transition">
                      <div className="size-1 rounded-full bg-slate-200"></div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">
                No notifications
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
